import type {
  ClientAcceptPayload,
  ClientInvitationCreatePayload,
  InvitationAcceptResult,
  InvitationCreated,
  InvitationDetail,
  InvitationListItem,
  Member,
  OrganizationAcceptPayload,
  OrganizationInvitationCreatePayload,
  UserAcceptPayload,
  UserInvitationCreatePayload,
  WorkspaceInvitationAcceptPayload,
  WorkspaceInvitationCreatePayload,
} from "@/types/invitation";

import { api, unwrap } from "./axiosConfig";
import type { OrganizationCertificate } from "./organizationService";

export type {
  WorkspaceInvitationAcceptPayload,
  WorkspaceInvitationPrefill,
} from "@/types/invitation";

/** Corpo do aceite público, discriminado pelo tipo do convite. Espelha os
 * *AcceptRequest do backend (app/schemas/invitations/requests.py). */
export type InvitationAcceptPayload =
  | WorkspaceInvitationAcceptPayload
  | UserAcceptPayload
  | OrganizationAcceptPayload
  | ClientAcceptPayload;

const ws = (id: number) => `/api/v1/workspaces/${id}`;

export const invitationService = {
  // ─── User invitations ───────────────────────────────────────────
  listUser(workspaceId: number): Promise<InvitationListItem[]> {
    return unwrap(api.get(`${ws(workspaceId)}/invitations`));
  },
  createUser(workspaceId: number, payload: UserInvitationCreatePayload): Promise<InvitationCreated> {
    return unwrap(api.post(`${ws(workspaceId)}/invitations`, payload));
  },
  revokeUser(workspaceId: number, id: number): Promise<void> {
    return api.delete(`${ws(workspaceId)}/invitations/${id}`).then(() => undefined);
  },

  // ─── Organization invitations ───────────────────────────────────
  listOrg(workspaceId: number): Promise<InvitationListItem[]> {
    return unwrap(api.get(`${ws(workspaceId)}/organization-invitations`));
  },
  createOrg(
    workspaceId: number,
    payload: OrganizationInvitationCreatePayload,
  ): Promise<InvitationCreated> {
    return unwrap(api.post(`${ws(workspaceId)}/organization-invitations`, payload));
  },
  revokeOrg(workspaceId: number, id: number): Promise<void> {
    return api.delete(`${ws(workspaceId)}/organization-invitations/${id}`).then(() => undefined);
  },

  // ─── Client invitations ─────────────────────────────────────────
  listClient(workspaceId: number): Promise<InvitationListItem[]> {
    return unwrap(api.get(`${ws(workspaceId)}/client-invitations`));
  },
  createClient(
    workspaceId: number,
    payload: ClientInvitationCreatePayload,
  ): Promise<InvitationCreated> {
    return unwrap(api.post(`${ws(workspaceId)}/client-invitations`, payload));
  },
  revokeClient(workspaceId: number, id: number): Promise<void> {
    return api.delete(`${ws(workspaceId)}/client-invitations/${id}`).then(() => undefined);
  },

  // ─── Workspace invitations (platform) ───────────────────────────
  listWorkspace(): Promise<InvitationListItem[]> {
    return unwrap(api.get(`/api/v1/admin/workspaces/invitations`));
  },
  createWorkspace(payload: WorkspaceInvitationCreatePayload): Promise<InvitationCreated> {
    return unwrap(api.post(`/api/v1/admin/workspaces/invitations`, payload));
  },
  revokeWorkspace(id: number): Promise<void> {
    return api.delete(`/api/v1/admin/workspaces/invitations/${id}`).then(() => undefined);
  },

  // ─── Public (no auth) ───────────────────────────────────────────
  getDetail(token: string): Promise<InvitationDetail> {
    return unwrap(api.get(`/api/v1/invitations/${token}`));
  },
  accept(token: string, body: InvitationAcceptPayload): Promise<InvitationAcceptResult> {
    return unwrap(api.post(`/api/v1/invitations/${token}/accept`, body));
  },
  /** SEC-021: upload público do certificado A1 (.pfx) atrelado ao TOKEN do convite
   * de organização (pós-accept). Não exige sessão verificada — o owner recém-criado
   * não bate na rota gated /{ws}/organizations/{org}/certificate (que daria 403). */
  uploadCertificate(token: string, file: File, password: string): Promise<OrganizationCertificate> {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("password", password);
    return unwrap(api.post(`/api/v1/invitations/${token}/certificate`, fd));
  },
};

export const memberService = {
  list(workspaceId: number): Promise<Member[]> {
    return unwrap(api.get(`${ws(workspaceId)}/members`));
  },
  revoke(workspaceId: number, grantId: number): Promise<void> {
    return api.delete(`${ws(workspaceId)}/access-grants/${grantId}`).then(() => undefined);
  },
};
